import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Court, CreateCourt, VenueId } from "../../schemas/venue";
import { venueApi } from "../../venue-api";
import { queryKeys } from "../query-keys";

const keys = queryKeys.venuePortal;

/** A hall's courts are one POST each; a few at a time keeps the batch quick without a burst. */
const COURT_BATCH_CONCURRENCY = 4;

/** Either every court was created, or the batch stopped at the first failure. */
export type CourtBatch =
	| { kind: "complete"; created: Court[] }
	| { kind: "stopped"; created: Court[]; error: unknown };

/**
 * Creates `bodies` with a few requests in flight. After the first failure no new
 * request starts; requests already in flight finish and are reported, so the
 * caller always knows exactly which courts exist. `created` keeps the order asked.
 */
export async function createCourtsInBatch(
	create: (body: CreateCourt) => Promise<Court>,
	bodies: readonly CreateCourt[],
	{
		concurrency = COURT_BATCH_CONCURRENCY,
		onProgress,
	}: { concurrency?: number; onProgress?: (done: number) => void } = {},
): Promise<CourtBatch> {
	const slots: (Court | undefined)[] = bodies.map(() => undefined);
	const failures: unknown[] = [];
	let next = 0;
	let done = 0;

	async function worker() {
		while (failures.length === 0 && next < bodies.length) {
			const index = next++;
			const body = bodies[index];
			if (body === undefined) return;
			try {
				slots[index] = await create(body);
				onProgress?.(++done);
			} catch (error) {
				failures.push(error);
			}
		}
	}

	await Promise.all(Array.from({ length: Math.min(concurrency, bodies.length) }, worker));

	const created = slots.filter((court): court is Court => court !== undefined);
	return failures.length === 0
		? { created, kind: "complete" }
		: { created, error: failures[0], kind: "stopped" };
}

/**
 * Adds many courts at once. The mutation resolves with the batch even when it
 * stopped early, and the lists are re-read once at the end, not once per court.
 */
export function useCreateVenueCourts(venueId: VenueId) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({
			courts,
			onProgress,
		}: {
			courts: readonly CreateCourt[];
			onProgress?: (done: number) => void;
		}) =>
			createCourtsInBatch((body) => venueApi.courts.create(venueId, body), courts, { onProgress }),
		onSuccess: () =>
			Promise.all([
				qc.invalidateQueries({ queryKey: keys.courts(venueId) }),
				qc.invalidateQueries({ queryKey: keys.venues() }),
			]),
	});
}
